import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/queryClient";
import { setUserProperties } from "@/lib/mixpanel";
import { identifyPostHogUser } from "@/lib/posthog";
import { Illustration } from "@/components/Illustration";

const ACTIVE_GRADES = [4, 5, 6, 7, 8, 9, 10, 11];

export function GradeMigrationModal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [pickedGrade, setPickedGrade] = useState<number | null>(null);

  const currentGrade = user?.grade ?? null;
  const needsConfirmation = user && !user.gradeConfirmedAt;

  const confirm = useMutation({
    mutationFn: async (grade: number) => {
      const res = await apiRequest("POST", "/api/profile/confirm-grade", { grade });
      return res.json();
    },
    onSuccess: (data) => {
      if (user) {
        setUserProperties({ grade: data.grade });
        identifyPostHogUser(user.id, { grade: data.grade });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
  });

  if (!needsConfirmation || currentGrade == null) return null;

  const selectedGrade = pickedGrade ?? currentGrade;
  const changed = selectedGrade !== currentGrade;

  return (
    <Dialog open>
      <DialogContent
        className="max-w-sm w-full p-0 overflow-hidden gap-0 flex flex-col [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="bg-primary/5 border-b px-6 pt-6 pb-5 text-center">
          <Illustration name="owl-cap" size={120} className="mx-auto" />
          <h2 className="mt-3 text-xl font-bold leading-tight">Quick check!</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Come September, what grade will you be in?
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="text-sm text-muted-foreground">
            We have you down for <span className="font-semibold text-foreground">Grade {currentGrade}</span>.
            If that's not right, pick your actual grade below.
          </div>

          <Select
            value={String(selectedGrade)}
            onValueChange={(v) => setPickedGrade(parseInt(v, 10))}
          >
            <SelectTrigger className="w-full" data-testid="select-grade">
              <SelectValue placeholder="Choose your grade" />
            </SelectTrigger>
            <SelectContent>
              {ACTIVE_GRADES.map((g) => (
                <SelectItem key={g} value={String(g)}>
                  Grade {g}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {confirm.isError && (
            <p className="text-sm text-destructive">
              {(confirm.error as any)?.status === 401
                ? "Your session has expired. Please log in again."
                : "Something went wrong. Please try again."}
            </p>
          )}
        </div>

        <div className="px-6 pb-6">
          <Button
            onClick={() => confirm.mutate(selectedGrade)}
            disabled={confirm.isPending}
            className="w-full rounded-lg"
            data-testid="button-confirm-grade"
          >
            {confirm.isPending
              ? "Saving..."
              : changed
                ? `Switch me to Grade ${selectedGrade}`
                : `Yes, Grade ${selectedGrade}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
